<template>
  <div v-if="chapter.assets.length >= 1" class="chapter-container panel">
    <div @click="showSectionContent = !showSectionContent" class="chapter-header clickable">
      <div class="chapter-header-details">
        <div class="chapter-title-summary-container">
          <h4>
            <b v-if="isCustomChapter" class="number">{{ chapter.number }}. </b>
          <i v-else class="fas k-asset-icon" :class="getDefaultChapterIcon(chapter.name)" aria-hidden="true"></i>
            <b class="title">{{ chapter.name }}</b>
          </h4>
          <p v-if="chapter.short_description" class="chapter-short-description">{{ chapter.short_description }}</p>
        </div>
        <k-progress v-if="mandatoryAssets.length > 0"
          shape="bar"
          :percentage="chapterProgress"
          :precision="1"
          :show-bar-text="true"
          :valueMax="chapter.assets.length"
          customText="Completed"
          progress-theme="dark"
          ariaLabel="Overall Chapter Completion"
          size="small"
        ></k-progress>
      </div>
      <i class="toggle fas" :class="toggleClass" aria-hidden="true"></i>
    </div>
    <div v-if="showSectionContent" class="chapter-asset">
      <div v-if="showSectionContent" class="chapter-assets">
        <table class="chapter-assets-table">
          <tbody>
            <tr v-for="(asset, index) in assets" :key="asset.number" :class="assetDetailsClass(asset.asset_type)">
                <!-- Learning plan status icon -->
              <td v-if="displayLearningPlanSection" class="asset-plan-status">
                <i v-if="!asset.is_standard_programme_asset && asset.is_accessible"
                  class="fas fa-file-plus"
                  title="Additional asset and is accessible"
                 aria-hidden="true"></i>
                <i v-else-if="asset.is_standard_programme_asset && !asset.is_accessible"
                  class="fas fa-eye-slash"
                  title="Module asset but not accessible"
                 aria-hidden="true"></i>
                <i v-else-if="asset.is_standard_programme_asset && asset.is_accessible"
                  class="fas fa-eye"
                  title="Module asset and is accessible"
                 aria-hidden="true"></i>
              </td>
              <!-- Asset details -->
              <td class="asset-icon">
                <i class="fas k-asset-icon" :class="getAssetIcon(asset.asset_type)" aria-hidden="true"></i>
              </td>
              <td class="asset-title">
                <router-link v-if="hasSubmissions(index)" class="asset-title-link" :to="getFeedbackPath(index)">
                  {{ asset.name }} <i class="fas fa-external-link-alt" aria-hidden="true"></i>
                </router-link>
                <span v-else>{{ asset.name }}</span>
                <k-asset-tags :asset="asset"></k-asset-tags>
              </td>
              <!-- Learning plan Remove/Enable/Disable button -->
              <td v-if="displayLearningPlanSection && canManageModuleAssets" class="asset-plan-action">
                <button
                  v-if="asset.is_standard_programme_asset && !asset.is_accessible"
                  class="btn btn-success table-action-btn"
                  @click="confirmToggleAssetAccessibility(asset)"
                >
                  Enable
                </button>
                <button
                 v-else-if="!asset.is_standard_programme_asset && asset.is_accessible"
                 class="btn btn-danger table-action-btn"
                 @click="confirmToggleAssetAccessibility(asset)"
                 >
                  Remove
                </button>
                <button
                  v-else
                  class="btn btn-danger table-action-btn"
                  @click="confirmToggleAssetAccessibility(asset)"
                >
                  Disable
                </button>
              </td>
              <!-- Event / meeting date -->
              <td v-if="['meeting', 'calendar_event'].includes(asset.asset_type)" class="asset-date" >
                <span>{{ eventDateText(index) }}</span>
              </td>
              <!-- Check and Score -->
              <td v-if="asset.is_complete">
                <i class="fas fa-check" aria-hidden="true"></i>
              </td>
              <td v-if="asset.progress_score || asset.latest_score" class="asset-score">
                <span :class="progressColour(index)">
                  {{ scoreFormat(index) }} %
                </span>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-if="displayLearningPlanSection && canManageModuleAssets" class="add-asset-container">
          <button class="btn btn-primary add-asset-btn" @click="toggleAssetModal">
            <i class="fas fa-plus" aria-hidden="true"></i> Add Asset
          </button>
        </div>
      </div>
    </div>
    <add-asset-modal
      v-if="showAddAssetModal && displayLearningPlanSection && canManageModuleAssets"
      :userId="userId"
      :moduleId="moduleId"
      :showModal="showAddAssetModal"
      @close="toggleAssetModal"
      @refresh-modules="refreshModules"
    />
  </div>
</template>

<style scoped>
.chapter-container {
  margin: 15px;
  box-shadow: var(--box-shadow);
  transition: all 0.2s ease-in-out;
}

.chapter-container:hover {
  background-color: var(--kate-background-alt-alpha);
}

.chapter-container:active {
  box-shadow: var(--box-shadow);
  background-color: var(--kate-background-alt-alpha);
}

.chapter-header {
  padding: 15px;
  display: flex;
  gap: 15px;
  justify-content: space-between;
}

.chapter-title-summary-container {
  display: flex;
  flex-direction: column;
}

.chapter-header-details {
  display: flex;
  gap: 15px;
  justify-content: space-between;
  flex: 1;
}

.chapter-header .k-progress.squared {
  min-width: 140px;
}

.chapter-header-details .k-progress {
  width: 30%;
}

.chapter-asset .fa-check {
  color: var(--kate-success);
}

.chapter-asset {
  padding: 15px;
}

.asset-score span {
  color: var(--kate-type-dark);
  padding: 3px 5px;
  border-radius: 4px;
  text-align: center;
}

.asset-score .success {
  background-color: var(--kate-success);
}

.asset-score .warning {
  background-color: var(--kate-warning);
}

.asset-entry.k-asset-icon {
  margin-right: 5px;
}

.asset-score .danger {
  background-color: var(--kate-danger);
}

.table-action {
  text-align: right;
}

.table-action-btn {
  font-size: 0.75em;
  padding: 5px 8px;
  border-radius: 4px;
  white-space: nowrap;
}

.tags {
  width: fit-content;
  display: inline-block;
  margin-left: 10px;
}

/* Table */
.chapter-assets {
  overflow-x: auto;
}

.chapter-assets-table {
  width: 100%;
  border-collapse: collapse;
}

.chapter-assets-table td {
  padding: 5px;
}

.chapter-assets-table tr:hover {
  background-color: var(--kate-background-body-alpha);
}

.asset-icon {
  width: 10px;
}

.asset-title {
  width: 25vw;
  min-width: 300px;
}

.asset-plan-status {
  width: 10px;
  text-align: center;
}

.asset-plan-status i {
  cursor: pointer;
  color: var(--kate-type-secondary);
}

.asset-plan-status i:hover {
  color: var(--kate-type-primary);
}

.asset-plan-status i.fa-trash {
  color: var(--kate-danger);
}

.asset-plan-status i.fa-trash:hover {
  color: var(--kate-danger-dark);
}

.asset-plan-status i.fa-eye-slash {
  color: var(--kate-warning);
}

.asset-plan-status i.fa-eye-slash:hover {
  color: var(--kate-warning-dark);
}

.asset-plan-status i.fa-eye,
i.fa-file-plus {
  color: var(--kate-success);
}

.asset-plan-status i.fa-eye:hover,
i.fa-file-plus:hover {
  color: var(--kate-success-dark);
}

td.asset-date,
td.asset-meeting-link,
td.form-entry.table-input,
td.form-entry.table-calendar {
  min-width: 190px;
}

.chapter-asset-details .asset-score {
  width: 70px;
  text-align: center;
}

.add-asset-container {
  display: flex;
  justify-content: flex-end;
  margin-top: 15px;
  margin-bottom: 10px;
}

.add-asset-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
}
</style>

<script>
import useGlobalStore from '@stores/global';
import ModuleChapterPanelMixin from '@mixins/single-chapter-mixin';
import KProgress from '@base-components/k-progress.vue';
import KAssetTags from '@base-components/k-asset-tags.vue';
import timeMixins from '@mixins/time-mixins';
import AddAssetModal from './add-asset-modal.vue';
import { ASSET_TYPES } from '../../../constants';

export default {
  components: {
    KProgress,
    KAssetTags,
    AddAssetModal,
  },

  mixins: [ModuleChapterPanelMixin, timeMixins],

  props: {
    chapter: Object,
    userId: Number,
    programmeMemberId: Number,
    moduleId: {
      type: Number,
      required: true,
    },
    isLearnerSupport: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      store: useGlobalStore(),
      showSectionContent: true,
      showAddAssetModal: false,
    };
  },

  computed: {
    displayLearningPlanSection() {
      return this.$featureFlags.isEnabled('kate-learning-plan');
    },
    canManageModuleAssets() {
      return this.$permissions.hasPermission('manage_user_module_assets');
    },
  },

  methods: {
    assetDetailsClass(assetType) {
      if (assetType === 'meeting') {
        return 'chapter-asset-details meeting';
      }
      return 'chapter-asset-details';
    },

    eventDateText(index) {
      if (this.assets[index].meeting_start) {
        let dateStr = this.parseTimestamp(this.assets[index].meeting_start);
        if (this.assets[index].meeting_end) {
          dateStr += ` - ${this.parseTimestamp(this.assets[index].meeting_end)}`;
        }
        return dateStr;
      }
      if (this.assets[index].event_start) {
        return this.parseTimestamp(this.assets[index].event_start);
      }
      return 'Not yet scheduled';
    },

    hasSubmissions(index) {
      if (ASSET_TYPES[this.assets[index].asset_type]?.completion_type === 'submission') {
        return this.assets[index].latest_timestamp !== null;
      }
      return false;
    },

    progressColour(index) {
      if (this.assets[index].latest_score > 75) {
        return 'success';
      }
      if (this.assets[index].latest_score < 30) {
        return 'danger';
      }
      return 'warning';
    },

    scoreFormat(index) {
      return (Math.round(10 * this.assets[index].latest_score)) / 10;
    },

    getFeedbackPath(index) {
      let output = {};
      if (this.assets[index].asset_type === 'pak') {
        output = {
          name: 'dash_pak_ov_feedback',
          params: {
            studentId: this.userId,
            modulePakId: this.assets[index].id,
          },
        };
      } else if (this.assets[index].asset_type === 'quiz') {
        output = {
          name: 'dash_quiz_feedback',
          params: {
            studentId: this.userId,
            moduleQuizId: this.assets[index].id,
          },
        };
      } else if (this.assets[index].asset_type === 'questionnaire') {
        output = {
          name: 'dash_questionnaire_feedback',
          params: {
            studentId: this.userId,
            moduleQuestionnaireId: this.assets[index].id,
          },
        };
      }
      return output;
    },
    toggleAssetStatus(asset) {
      if (asset.is_accessible) {
        this.$http.delete(
          `/api/curriculum/admin/${asset.asset_type}/module/${asset.id}/learner/${this.userId}`,
        ).then(() => {
          this.$logger.info('Successfully enabled access to asset for the learner', { studentId: this.userId, asset }, true);
          this.refreshModules();
        }).catch(err => {
          if (this.$http.isWarning(err)) {
            this.$logger.warn('There was an error while revoking the asset', { studentId: this.userId, asset }, err);
          } else {
            this.$logger.error('There was an error while revoking the asset', { studentId: this.userId, asset }, err);
          }
          this.$ktoast.showError(err);
        });
      } else {
        this.$http.post(
          `/api/curriculum/admin/${asset.asset_type}/module/${asset.id}/learner/${this.userId}`,
        ).then(() => {
          this.$logger.info('Successfully enabled access to asset for the learner', { studentId: this.userId, asset }, true);
          this.refreshModules();
        }).catch(err => {
          if (this.$http.isWarning(err)) {
            this.$logger.warn('There was an error while enabling the asset', { studentId: this.userId, asset }, err);
          } else {
            this.$logger.error('There was an error while enabling the asset', { studentId: this.userId, asset }, err);
          }
          this.$ktoast.showError(err);
        });
      }
    },
    confirmToggleAssetAccessibility(asset) {
      let message = '';

      if (!asset.is_standard_programme_asset && asset.is_accessible) {
        // additional asset case
        message = `Do you want to remove additional asset "${asset.name}"?`;
      } else if (asset.is_standard_programme_asset && !asset.is_accessible) {
        // normal module grant back access
        message = `Do you want to grant back access to the module asset "${asset.name}"?`;
      } else if (asset.is_standard_programme_asset && asset.is_accessible) {
        // normal module revoke access
        message = `Do you want to revoke access to module asset "${asset.name}"?`;
      }
      this.$ktoast.confirmToast(
        message,
        'warning',
        () => this.toggleAssetStatus(asset),
      );
    },
    toggleAssetModal() {
      this.showAddAssetModal = !this.showAddAssetModal;
    },
    refreshModules() {
      this.$emit('refresh-modules');
    },
  },
};
</script>
